import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { X } from "@phosphor-icons/react";
import { buildLocalizedPath } from "../lib/langRoutes";

// Non-sensitive UX preference only (consent status). Safe to keep in localStorage.
const STORAGE_KEY = "kosmedico_cookie_consent";

const isDev = process.env.NODE_ENV !== "production";

export const CookieBanner = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] || "de";
  const [visible, setVisible] = useState(false);
  // Guard against a StrictMode / late effect re-showing the banner after the
  // user has already dismissed it in the current session (e.g. when
  // localStorage is unavailable and we cannot rely on the persisted flag).
  const dismissedInSession = useRef(false);

  useEffect(() => {
    if (dismissedInSession.current) return;
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch (error) {
      // Safari private mode or storage disabled — show banner as a safe default.
      if (isDev) console.warn("[CookieBanner] read failed", error);
      setVisible(true);
    }
  }, []);

  const persist = (value) => {
    // Mark as dismissed BEFORE any side effect so a follow-up re-render can
    // never resurrect the banner even if setItem or dataLayer.push throw.
    dismissedInSession.current = true;
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (error) {
      if (isDev) console.warn("[CookieBanner] write failed", error);
    }
    if (value === "accepted") {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "cookie_consent_accepted" });
        if (typeof window.gtag === "function") {
          window.gtag("consent", "update", {
            analytics_storage: "granted",
            ad_storage: "denied",
            ad_user_data: "denied",
            ad_personalization: "denied",
          });
        }
      } catch (error) {
        if (isDev) console.warn("[CookieBanner] dataLayer push failed", error);
      }
    } else if (value === "declined" && typeof window.gtag === "function") {
      try {
        window.gtag("consent", "update", { analytics_storage: "denied" });
      } catch (error) {
        if (isDev) console.warn("[CookieBanner] consent update failed", error);
      }
    }
    // Hide last so no earlier throw can cancel the commit.
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 inset-x-0 z-[70] bg-[#1F2D1D] text-[#F6F5F2] border-t border-[#A7CA99]/30 shadow-[0_-10px_30px_-10px_rgba(0,0,0,0.4)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row md:items-center gap-5">
        <p className="text-sm font-light leading-relaxed flex-1">
          {t("cookies.text", "Usamos cookies essenciais e de análise para melhorar a sua experiência. Ao continuar, aceita a nossa")} {" "}
          <Link to={buildLocalizedPath("datenschutz", lang)} className="underline hover:text-[#A7CA99]">
            {t("cookies.policy_link", "Política de Privacidade")}
          </Link>.
        </p>
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => persist("declined")}
            data-testid="cookie-decline"
            className="text-xs uppercase tracking-[0.18em] text-[#F6F5F2]/70 hover:text-[#F6F5F2] transition-colors"
          >
            {t("cookies.decline", "Apenas essenciais")}
          </button>
          <button
            type="button"
            onClick={() => persist("accepted")}
            data-testid="cookie-accept"
            className="px-5 py-3 bg-[#A7CA99] hover:bg-[#F6F5F2] text-[#1F2D1D] text-xs uppercase tracking-[0.2em] font-medium transition-colors"
          >
            {t("cookies.accept", "Aceitar")}
          </button>
          <button
            type="button"
            onClick={() => persist("dismissed")}
            data-testid="cookie-close"
            aria-label="close"
            className="text-[#F6F5F2]/60 hover:text-[#F6F5F2] transition-colors"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
};
